/**
 * What a name or an Asset Identifier typed into the Equipment step's table will be saved as — the
 * line under the field, worked out as it is typed, with no server call (`equipment-page-table`, R8).
 *
 * The naming rule is the twin's *-1, -2* rule (`twinNames.ts` / `nameFamily.ts`): a second *Rower*
 * is *Rower-2*, and the lone *Rower* before it becomes *Rower-1*. The server stays the gate — a name
 * taken in another tab since the last read is refused with a 400, and the field says so (R10).
 *
 * Pure. Every answer is computed against `nameIndexWithout`: the twin's names with the row being
 * edited left out, so renaming *Treadmill-4* to *treadmill-4* reads as unchanged rather than taken.
 */
import { normaliseAssetUid, type FloorPlannerLayout } from '@repo/frontend-types';
import {
  buildTwinNameIndex,
  isNameTaken,
  lowestFreeNumber,
  nameFamily,
  nameForNewcomer,
  nameKey,
  nextFreeName,
  registerRowName,
  type TwinNameIndex,
  type TwinNameRegisterRow,
} from '@repo/front-end-utils';

/** The row fields the answers read. */
export interface EditableRow {
  id: number;
  name: string;
  asset_name?: string | null;
  customer_asset_identifier?: string | null;
}

/**
 * The twin's names without the row `rowId`. A register row that is on the plan is entered ONCE, as
 * its plan item (`buildTwinNameIndex` skips the register row and keys the item by its `assetUid`),
 * so leaving the row out of `registerRows` is not enough: its item's entry goes too.
 *
 * Pass the register-joined layout, whose items carry the register's names.
 */
export const nameIndexWithout = (
  layout: FloorPlannerLayout | null | undefined,
  registerRows: readonly TwinNameRegisterRow[],
  rowId: number | null,
): TwinNameIndex => {
  if (rowId === null) return buildTwinNameIndex(layout, registerRows);
  const index = buildTwinNameIndex(
    layout,
    registerRows.filter(row => row.id !== rowId),
  );
  const uid = normaliseAssetUid(rowId);
  const byKey: TwinNameIndex['byKey'] = new Map();
  // `forEach`, never `for…of`: this app compiles to ES5 without downlevelIteration, where a
  // for…of over a Map iterates nothing.
  index.byKey.forEach((entries, key) => {
    const kept = entries.filter(
      entry =>
        !(uid !== undefined && entry.assetUid === uid) &&
        !(entry.ref.type === 'register' && entry.ref.assetId === rowId),
    );
    if (kept.length > 0) byKey.set(key, kept);
  });
  return { byKey };
};

export type NameAnswer =
  /** The same name, ignoring capitals and outer spaces: nothing to send. */
  | { kind: 'unchanged'; name: string; line: null }
  /** Nothing typed: refused. */
  | { kind: 'empty'; name: ''; line: string }
  /** Free as typed (trimmed). */
  | { kind: 'free'; name: string; line: null }
  /** Taken: saved under the family's next number, renaming nothing. */
  | { kind: 'numbered'; name: string; line: string }
  /** Taken by one unplaced register row: that row is renamed first, then this one is saved. */
  | {
      kind: 'holder';
      name: string;
      holder: { assetId: number; from: string; to: string };
      line: string;
    };

export const NAME_NEEDED = 'A name is needed.';

/** Whether an answer may be committed (Enter, Tab or blur writes it). */
export const nameAnswerCommits = (answer: NameAnswer): boolean =>
  answer.kind !== 'empty' && answer.kind !== 'unchanged';

/** The family's own spelling of `typed` — its bare holder's, else its first member's stem. */
const familySpelling = (typed: string, index: TwinNameIndex): string => {
  const { bare, members } = nameFamily(typed, index);
  const bareName = bare[0]?.name.trim();
  if (bareName) return bareName;
  const first = [...members].sort((a, b) => a.n - b.n)[0];
  return first ? first.entry.name.trim().replace(/-\d+$/, '') : typed;
};

/** *(1–4 are taken)* where the family is exactly 1…k; otherwise the name that is taken. */
const takenNote = (base: string, index: TwinNameIndex): string => {
  const { bare, members } = nameFamily(base, index);
  const numbers = Array.from(new Set(members.map(member => member.n))).sort((a, b) => a - b);
  const contiguous = numbers.length > 0 && numbers.every((n, i) => n === i + 1);
  if (bare.length === 0 && contiguous) {
    return numbers.length === 1 ? `(${base}-1 is taken)` : `(1–${numbers.length} are taken)`;
  }
  return `(${bare.length > 0 ? base : `${base}-${numbers[0]}`} is taken)`;
};

/**
 * The answer for `typed` on `row` (`null` for the add row's newcomer), against an index built by
 * `nameIndexWithout(layout, rows, row?.id ?? null)`.
 */
export const nameAnswer = (
  typed: string,
  row: EditableRow | null,
  index: TwinNameIndex,
): NameAnswer => {
  const trimmed = typed.trim();
  if (!trimmed) return { kind: 'empty', name: '', line: NAME_NEEDED };
  const current = row ? registerRowName(row) : null;
  const unchanged = (name: string): boolean =>
    current !== null && nameKey(name) === nameKey(current);
  if (unchanged(trimmed)) return { kind: 'unchanged', name: current ?? trimmed, line: null };

  const answer = ((): NameAnswer => {
    // A typed `<stem>-<n>` continues the stem's family, as the planner's offer does; it never
    // renames anything (a numbered holder is not a bare one).
    if (/^(.+)-(\d+)$/.test(trimmed)) {
      if (!isNameTaken(trimmed, index)) return { kind: 'free', name: trimmed, line: null };
      const name = nextFreeName(trimmed, index);
      return { kind: 'numbered', name, line: `Will be saved as ${name} (${trimmed} is taken).` };
    }
    const { bare, members } = nameFamily(trimmed, index);
    if (bare.length === 0 && members.length === 0) {
      return { kind: 'free', name: trimmed, line: null };
    }
    // The twin's capitals, not the typed ones: *rower* over *Rower* saves *Rower-2*.
    const base = familySpelling(trimmed, index);
    // A member of the family typed as its base already is that (*Rower-1* typed as *Rower*). The
    // index leaves this row out, so the holder's move would otherwise be to this row's own name,
    // which the server refuses (Copilot, PR #2157).
    const own = current === null ? '' : nameKey(current);
    const stem = `${nameKey(base)}-`;
    if (own.startsWith(stem) && /^\d+$/.test(own.slice(stem.length))) {
      return { kind: 'unchanged', name: current ?? trimmed, line: null };
    }
    const decision = nameForNewcomer(base, 'machine', index);
    const holder = decision.renameHolder;
    // Only an UNPLACED register row is renamed from this step. A placed row's entry is its plan
    // item, and a plan-only machine, a space or a floor is never renamed from here.
    if (holder && holder.entry.ref.type === 'register') {
      const from = holder.entry.name.trim();
      return {
        kind: 'holder',
        name: decision.name,
        holder: { assetId: holder.entry.ref.assetId, from, to: holder.to },
        line: `Will be saved as ${decision.name}, and ${from} becomes ${holder.to}.`,
      };
    }
    /*
     * Not renaming the holder, so its number is not reserved: the newcomer takes the LOWEST free
     * -n (n >= 2). `decision.name` was chosen with the holder's -n held back for a rename this
     * step will not make, and would skip a free number.
     */
    const name = holder ? `${base}-${lowestFreeNumber(base, index, 2)}` : decision.name;
    return {
      kind: 'numbered',
      name,
      line: `Will be saved as ${name} ${takenNote(base, index)}.`,
    };
  })();
  // Editing *Chest Press-2* to *Chest Press* answers *Chest Press-2*: it already is that.
  if (answer.kind !== 'empty' && unchanged(answer.name)) {
    return { kind: 'unchanged', name: current ?? answer.name, line: null };
  }
  return answer;
};

export type IdentifierAnswer =
  | { kind: 'unchanged'; value: string | null; line: null }
  | { kind: 'clear'; value: null; line: string }
  | { kind: 'taken'; value: string; line: string }
  | { kind: 'free'; value: string; line: null };

export const IDENTIFIER_CLEARED = 'The Asset Identifier will be cleared.';

export const identifierAnswerCommits = (answer: IdentifierAnswer): boolean =>
  answer.kind === 'clear' || answer.kind === 'free';

/** The database's rule for "the same identifier": trimmed and upper-cased. */
const identifierKey = (value: string | null | undefined): string =>
  (value ?? '').trim().toUpperCase();

/** The answer for an Asset Identifier typed on `row`, against every register row. */
export const identifierAnswer = (
  typed: string,
  row: EditableRow | null,
  rows: readonly EditableRow[],
): IdentifierAnswer => {
  const trimmed = typed.trim();
  const current = row?.customer_asset_identifier?.trim() || null;
  if (trimmed === (current ?? '')) return { kind: 'unchanged', value: current, line: null };
  if (!trimmed) return { kind: 'clear', value: null, line: IDENTIFIER_CLEARED };
  const key = identifierKey(trimmed);
  const holder = rows.find(
    other => other.id !== row?.id && identifierKey(other.customer_asset_identifier) === key,
  );
  if (holder) {
    return { kind: 'taken', value: trimmed, line: `Already used by ${registerRowName(holder)}.` };
  }
  return { kind: 'free', value: trimmed, line: null };
};
